import { useRef, useState, useEffect, useCallback } from "react";
import { Upload, LogIn, X } from "lucide-react";
import { toast } from "sonner";

const CompactVinyl = ({ file, onFileSelect, onFileClear, trackNumber, analysis, rotation, onRotationChange, onSnapComplete, isPlaying, bpmAdjustment, isLoggedIn, onRequireLogin }) => {
  const inputRef = useRef(null);
  const discRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startAngle, setStartAngle] = useState(0);

  const handleFileInput = () => {
    inputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) {
      // 🛡️ BUTTAFUORI INTELLIGENTE: Controlla sia la "carta d'identità" (MIME) che il nome
      const fileName = f.name.toLowerCase();
      const isAudioExt = fileName.endsWith('.mp3') || fileName.endsWith('.wav') || fileName.endsWith('.m4a') || fileName.endsWith('.ogg') || fileName.endsWith('.flac') || fileName.endsWith('.aac') || fileName.endsWith('.mp4');
      const isAudioType = f.type.startsWith('audio/') || f.type.startsWith('video/');
      
      if (isAudioType || isAudioExt) {
        onFileSelect(f);
      } else {
        toast.error("Please select an audio file (Recognized type: " + (f.type || "Unknown") + ")");
      }
    }
    // Reset input to allow re-selecting same file
    e.target.value = '';
  };

  const handleFileClear = (e) => {
    e.stopPropagation();
    if (onFileClear) {
      onFileClear();
      toast.success(`Track ${trackNumber} cleared`);
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) {
      const fileName = f.name.toLowerCase();
      const isAudioExt = fileName.endsWith('.mp3') || fileName.endsWith('.wav') || fileName.endsWith('.m4a') || fileName.endsWith('.ogg') || fileName.endsWith('.flac') || fileName.endsWith('.aac') || fileName.endsWith('.mp4');
      const isAudioType = f.type.startsWith('audio/') || f.type.startsWith('video/');
      
      if (isAudioType || isAudioExt) {
        onFileSelect(f);
      } else {
        toast.error("Please drop an audio file");
      }
    }
  };

  const getAngle = (clientX, clientY) => {
    if (!discRef.current) return 0;
    const rect = discRef.current.getBoundingClientRect();
    return Math.atan2(clientY - (rect.top + rect.height / 2), clientX - (rect.left + rect.width / 2)) * (180 / Math.PI);
  };

  const snapToBeat = useCallback((currentRotation) => {
    if (!analysis?.beat_timestamps?.length) return currentRotation;
    const time = (currentRotation / 360) * analysis.duration;
    let nearestBeat = analysis.beat_timestamps[0], minDiff = Math.abs(time - nearestBeat);
    for (const beat of analysis.beat_timestamps) {
      const diff = Math.abs(time - beat);
      if (diff < minDiff) { minDiff = diff; nearestBeat = beat; }
    }
    return (nearestBeat / analysis.duration) * 360;
  }, [analysis]);

  const handleStart = (e) => {
    if (!file || !analysis) return;
    e.preventDefault();
    setIsDragging(true);
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    setStartAngle(getAngle(clientX, clientY) - rotation);
  };

  const handleMove = useCallback((e) => {
    if (!isDragging) return;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    let newRot = getAngle(clientX, clientY) - startAngle;
    while (newRot < 0) newRot += 360;
    while (newRot >= 360) newRot -= 360;
    onRotationChange(newRot, false);
  }, [isDragging, startAngle, onRotationChange]);

  const handleEnd = useCallback(() => {
    if (isDragging && analysis) {
      const snapped = snapToBeat(rotation);
      onRotationChange(snapped, true);
      onSnapComplete?.(snapped);
    }
    setIsDragging(false);
  }, [isDragging, analysis, rotation, snapToBeat, onRotationChange, onSnapComplete]);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMove);
      window.addEventListener('mouseup', handleEnd);
      window.addEventListener('touchmove', handleMove);
      window.addEventListener('touchend', handleEnd);
      return () => {
        window.removeEventListener('mousemove', handleMove);
        window.removeEventListener('mouseup', handleEnd);
        window.removeEventListener('touchmove', handleMove);
        window.removeEventListener('touchend', handleEnd);
      };
    }
  }, [isDragging, handleMove, handleEnd]);

  return (
    <div className="compact-vinyl-wrapper" data-testid={`vinyl-${trackNumber}`}>
      <input 
        ref={inputRef} 
        type="file" 
        accept="audio/*,video/mp4,.mp3,.wav,.m4a,.ogg,.flac,.aac,.mp4" 
        onChange={handleFileChange} 
        style={{ display: 'none' }} 
      />
      <div
        ref={discRef}
        className={`compact-vinyl ${file ? 'has-file' : ''} ${isDragging ? 'dragging' : ''} ${isPlaying ? 'playing' : ''}`}
        onMouseDown={file && analysis ? handleStart : undefined}
        onTouchStart={file && analysis ? handleStart : undefined}
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleFileDrop}
        onClick={!file ? handleFileInput : undefined}
      >
        <div className="vinyl-grooves-compact" style={{ transform: `rotate(${rotation}deg)` }}>
          {[...Array(6)].map((_, i) => <div key={i} className="groove-compact" style={{ transform: `rotate(${i * 60}deg)` }} />)}
        </div>
        <div className="vinyl-center-compact">
          {file ? (
            <span className="vinyl-filename">{file.name.slice(0, 8)}...</span>
          ) : (
            <Upload size={20} />
          )}
        </div>
        <div className="vinyl-marker" style={{ transform: `rotate(${rotation}deg)` }} />
      </div>
      {file && (
        <button
          className="vinyl-delete-btn"
          onClick={handleFileClear}
          data-testid={`delete-track-${trackNumber}`}
          title={`Clear Track ${trackNumber}`}
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
};

export default CompactVinyl;